import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowUp, Sparkles, Bed, Accessibility, Icon, Wand2 } from "lucide-react";
import { BundleBuilderBanner } from "@/components/booking/BundleBuilderBanner";
import { stroller } from "@lucide/lab";
import { Fireflies } from "@/components/effects/Fireflies";
import { Reveal } from "@/components/effects/Reveal";
import { ProductCard } from "@/components/booking/ProductCard";
import { ProductDetailsModal } from "@/components/booking/ProductDetailsModal";
import { CompareBar, CompareModal } from "@/components/booking/CompareBar";
import { T, useSiteContent } from "@/context/SiteContent";

const StrollerIcon = (props) => <Icon iconNode={stroller} {...props} />;

const DEFAULT_CATEGORY_TILES = [
  { id: "strollers", titleKey: "catalog.tile.strollers.title", blurbKey: "catalog.tile.strollers.blurb", defaultTitle: "Strollers", defaultBlurb: "Singles, doubles & twins", Icon: StrollerIcon },
  { id: "baby-gear", titleKey: "catalog.tile.baby-gear.title", blurbKey: "catalog.tile.baby-gear.blurb", defaultTitle: "Baby Gear", defaultBlurb: "Sleep, meal, bath & play",   Icon: Bed },
  { id: "ecvs",      titleKey: "catalog.tile.ecvs.title",      blurbKey: "catalog.tile.ecvs.blurb",      defaultTitle: "ECVs",      defaultBlurb: "Electric vehicles — soon",   Icon: Accessibility, comingSoon: true },
];

export const CategoryBrowser = ({ products, categories, cart, cartOptions = {}, addToCart, range, unavailableIds, stockMap, availabilityLoading, onEditTrip, onRequestStock, byob, focusProductId, onFocusProductConsumed }) => {
  const [activeCat, setActiveCat] = useState("");
  const [activeSub, setActiveSub] = useState("");
  const [detailsProduct, setDetailsProduct] = useState(null);
  const [detailsScrollTo, setDetailsScrollTo] = useState(null);
  const openDetails = (p, opts = {}) => {
    setDetailsProduct(p);
    setDetailsScrollTo(opts.scrollTo || null);
  };

  // ---- Stroller comparison (max 2 at a time) ----
  const [compareIds, setCompareIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const compareProducts = compareIds.map((id) => products.find((p) => p.id === id)).filter(Boolean);
  const toggleCompare = (p) => {
    setCompareIds((cur) => {
      if (cur.includes(p.id)) return cur.filter((id) => id !== p.id);
      if (cur.length >= 2) {
        toast.error("You can compare 2 strollers at a time — remove one first.");
        return cur;
      }
      return [...cur, p.id];
    });
  };
  const gearListRef = useRef(null);
  const categoriesTopRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const datesPicked = !!(range && range.from && range.to);
  const soldOutSet = unavailableIds instanceof Set ? unavailableIds : new Set();
  const { get } = useSiteContent();
  const byobOn = !!byob?.enabled;
  const CATEGORY_TILES = DEFAULT_CATEGORY_TILES.map(t => ({
    ...t,
    title: get(t.titleKey, t.defaultTitle),
    blurb: get(t.blurbKey, t.defaultBlurb),
    // Auto-remove comingSoon if 1+ visible (non-addon) products exist for this category
    comingSoon: t.comingSoon && !products.some(p => p.category === t.id && !p.addon_only),
  }));

  // Deep link (?product=<id>) from the stroller quiz results — opens the
  // details modal for that product and lands the shopper in its category.
  useEffect(() => {
    const pid = new URLSearchParams(window.location.search).get("product");
    if (!pid || products.length === 0) return;
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    setActiveCat(p.category);
    setDetailsProduct(p);
    window.history.replaceState({}, "", window.location.pathname);
    setTimeout(() => gearListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
  }, [products]);

  // focusProductId — set by Home.jsx after TripSetup is saved when the shopper
  // arrived from a product page (e.g. /strollers/bj-gt2-single → Check Availability).
  // Opens the product details modal and scrolls the catalog into view.
  useEffect(() => {
    if (!focusProductId || products.length === 0) return;
    const p = products.find((x) => x.id === focusProductId);
    if (!p) return;
    setActiveCat(p.category);
    setDetailsProduct(p);
    onFocusProductConsumed?.();
    setTimeout(() => gearListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusProductId, products]);

  const handleCategorySelect = (id) => {
    const next = activeCat === id ? "" : id;
    setActiveCat(next);
    setActiveSub("");
    if (next) {
      requestAnimationFrame(() => {
        gearListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };

  const scrollToCategoryTop = () => {
    categoriesTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Show the sticky "back to categories" pill once the user has scrolled past
  // the tile row AND has picked a category. Wired via IntersectionObserver on
  // the categoriesTopRef so it costs nothing when idle.
  useEffect(() => {
    if (!activeCat || !categoriesTopRef.current) { setShowBackToTop(false); return; }
    const obs = new IntersectionObserver(
      ([entry]) => setShowBackToTop(!entry.isIntersecting),
      { rootMargin: "-80px 0px 0px 0px", threshold: 0 }
    );
    obs.observe(categoriesTopRef.current);
    return () => obs.disconnect();
  }, [activeCat]);

  const showByobBox = byobOn && activeCat === "baby-gear";
  const catRow = categories.find((c) => c.id === activeCat);
  const subs = catRow?.subcategories || [];
  // Products that match the active category+sub before availability filtering.
  const matched = products.filter((p) => {
    if (!activeCat) return false;
    if (p.addon_only) return false;
    if (p.category !== activeCat) return false;
    if (subs.length && activeSub && p.subcategory !== activeSub) return false;
    return true;
  });
  // Sold-out items stay visible (greyed out with a "Request it" button) but
  // sink to the end of the grid so in-stock gear is seen first.
  const filtered = datesPicked
    ? [...matched].sort((a, b) => Number(soldOutSet.has(a.id)) - Number(soldOutSet.has(b.id)))
    : matched;
  const soldOutCount = datesPicked ? matched.filter((p) => soldOutSet.has(p.id)).length : 0;

  return (
    <section id="catalog" className="section relative">
      <div className="section-tint" />
      <Fireflies count={12} />
      <div className="container">
        <div ref={categoriesTopRef} data-testid="catalog-top-anchor" className="scroll-mt-24" />
        <Reveal>
          <div className="flex justify-between items-end flex-wrap gap-6">
            <div>
              <p className="eyebrow mb-3"><T k="catalog.eyebrow" fallback="Curated gear · Twilight-tested" /></p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight max-w-3xl leading-tight">
                <T k="catalog.headline" fallback="Everything little wanderers need." />
              </h2>
            </div>
            {!datesPicked && (
              <button type="button" onClick={onEditTrip} className="text-[#FFD166] text-sm inline-flex items-center gap-1 hover:underline" data-testid="catalog-open-trip-setup">
                Pick dates first <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </Reveal>

        {/* Compact category tiles — 3-across on ALL screens (2xs+) so nothing pushes below the fold */}
        <div className="mt-8 grid grid-cols-3 gap-2 md:gap-3" data-testid="category-tiles">
          {CATEGORY_TILES.map((t) => {
            const isActive = activeCat === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => handleCategorySelect(t.id)}
                data-testid={`category-tile-${t.id}`}
                className={`text-center rounded-xl px-2 py-3 md:px-4 md:py-4 border transition-all relative ${isActive ? "border-[#FFD166] bg-[#FFD166]/10 shadow-[0_0_20px_rgba(255,209,102,0.18)]" : "border-[#FFD166]/25 bg-black/25 hover:border-[#FFD166]/60 hover:bg-black/40"}`}
              >
                {t.comingSoon && (
                  <span className="absolute top-1.5 right-1.5 text-[8px] uppercase tracking-[0.15em] text-[#040A0F] bg-[#FFD166] px-1.5 py-0.5 rounded-full font-medium">Soon</span>
                )}
                <div className="flex justify-center text-[#FFD166]">
                  <t.Icon className="w-6 h-6 md:w-7 md:h-7" strokeWidth={1.75} />
                </div>
                <div className="mt-1.5 font-serif text-sm md:text-base text-[#F6F1E4] leading-tight">{t.title}</div>
                <div className="text-[10px] md:text-xs text-[#B9C8D5] mt-0.5 leading-tight hidden sm:block">{t.blurb}</div>
                <div className={`mt-2 inline-flex items-center gap-1 text-[9px] md:text-[10px] uppercase tracking-[0.15em] ${isActive ? "text-[#FFD166]" : "text-[#B9C8D5]"}`}>
                  {isActive ? "Showing" : (t.comingSoon ? "Preview" : "Browse")} <ArrowRight className="w-3 h-3" />
                </div>
              </button>
            );
          })}
        </div>

        {/* Scroll anchor sits ABOVE the filter bar so selecting a category lands
            with the subcategory chips clearly in view */}
        <div ref={gearListRef} data-testid="catalog-products-anchor" className="scroll-mt-28 md:scroll-mt-32" />

        {/* Subcategory chips — sticky bar so the user can swap Singles/Doubles/etc without scrolling back up */}
        {activeCat && subs.length > 0 && (
          <div
            className="mt-6 sticky top-16 md:top-20 z-30 -mx-4 px-4 py-3 backdrop-blur-md bg-[#040A0F]/85 border-y border-[#FFD166]/15 flex flex-wrap gap-2 items-center"
            data-testid="subcategory-sticky-bar"
          >
            <span className="eyebrow text-[10px] mr-2 hidden sm:inline">Filter:</span>
            <button
              type="button"
              onClick={() => setActiveSub("")}
              data-testid="subcategory-chip-all"
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${!activeSub ? "bg-[#FFD166] text-[#040A0F] border-[#FFD166]" : "border-[#FFD166]/25 text-[#F6F1E4]/85 hover:border-[#FFD166]/60"}`}
            >
              All {catRow?.name}
            </button>
            {subs.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setActiveSub(s)}
                data-testid={`subcategory-chip-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${activeSub === s ? "bg-[#FFD166] text-[#040A0F] border-[#FFD166]" : "border-[#FFD166]/25 text-[#F6F1E4]/85 hover:border-[#FFD166]/60"}`}
              >
                {s}
              </button>
            ))}
            <button
              type="button"
              onClick={scrollToCategoryTop}
              className="ml-auto text-[10px] text-[#B9C8D5] hover:text-[#FFD166] inline-flex items-center gap-1"
              data-testid="subcategory-back-to-top"
            >
              <ArrowUp className="w-3 h-3" /> Back to top
            </button>
          </div>
        )}

        {showByobBox && (
          <BundleBuilderBanner byob={byob} products={products} cart={cart} range={range} />
        )}

        {/* Stroller quiz + comparison helper banner */}
        {activeCat === "strollers" && (
          <div
            className="mt-6 rounded-2xl border border-[#FFD166]/30 bg-gradient-to-r from-[#0A2438] to-[#0F1E2C] px-4 py-4 sm:px-6 flex items-center justify-between gap-4 flex-wrap"
            data-testid="stroller-quiz-banner"
          >
            <div className="flex items-center gap-3 min-w-0">
              <Wand2 className="w-5 h-5 text-[#FFD166] shrink-0" />
              <div className="min-w-0">
                <div className="font-serif text-lg text-[#F6F1E4] leading-tight">Not sure which stroller?</div>
                <div className="text-xs text-[#B9C8D5]">Take our 60-second quiz, or tap <span className="text-[#F4A93A]">Compare</span> on any two strollers.</div>
              </div>
            </div>
            <Link to="/stroller-quiz" className="btn-primary !py-2 !px-4 text-sm shrink-0" data-testid="stroller-quiz-link">
              Find my stroller <ArrowRight className="w-4 h-4 inline ml-1" />
            </Link>
          </div>
        )}

        {/* Availability status banner — visible when trip is set and we've done the check */}
        {activeCat && datesPicked && !availabilityLoading && soldOutCount > 0 && (
          <div
            className="mt-6 rounded-xl border border-[#F4A93A]/40 bg-[#F4A93A]/[0.06] px-4 py-3 flex items-center gap-3 text-sm text-[#F6F1E4]"
            data-testid="availability-hidden-banner"
          >
            <Sparkles className="w-4 h-4 text-[#F4A93A] shrink-0" />
            <div className="flex-1">
              {soldOutCount} {soldOutCount === 1 ? "item is" : "items are"} sold out for your dates.
              <span className="text-[#B9C8D5]"> Tap <span className="text-[#FFD166]">Request it</span> to be emailed if one frees up, or <button type="button" onClick={onEditTrip} className="text-[#FFD166] hover:underline">change dates</button>.</span>
            </div>
          </div>
        )}
        {activeCat && datesPicked && availabilityLoading && (
          <div
            className="mt-6 text-center text-xs text-[#B9C8D5]"
            data-testid="availability-loading"
          >
            Checking availability for your dates…
          </div>
        )}

        {activeCat ? (
          filtered.length === 0 ? (
            (
              <div className="mt-10 text-center py-12 border border-dashed border-[#FFD166]/40 rounded-2xl bg-[#FFD166]/[0.04] max-w-2xl mx-auto" data-testid="catalog-empty">
                <Sparkles className="w-7 h-7 mx-auto text-[#FFD166] mb-3" />
                <p className="eyebrow text-[#FFD166]">Coming Soon</p>
                <h3 className="font-serif text-2xl md:text-3xl text-[#F6F1E4] mt-2">
                  {catRow?.name} are on their way ✨
                </h3>
                <p className="text-[#B9C8D5] mt-3 max-w-md mx-auto text-sm">
                  Curating this collection now. Want a heads up the moment it goes live?
                  <a href="mailto:info@enchantedgearrentals.com" className="text-[#FFD166] hover:underline ml-1">Email us</a>.
                </p>
              </div>
            )
          ) : (
            /* 4-across on lg / 3 on md / 2 on sm — denser cards mean less scroll fatigue */
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-testid="catalog-products">
              {filtered.map((p, i) => (
                <Reveal key={p.id} delay={(i % 4) * 0.05}>
                  <ProductCard
                    p={p}
                    onSelect={addToCart}
                    selectedQty={cart[p.id] || 0}
                    range={range}
                    compact
                    soldOut={datesPicked && soldOutSet.has(p.id)}
                    availableCount={datesPicked && stockMap instanceof Map && stockMap.has(p.id) ? stockMap.get(p.id) : null}
                    onRequest={onRequestStock}
                    onOpenDetails={openDetails}
                    onToggleCompare={toggleCompare}
                    comparing={compareIds.includes(p.id)}
                  />
                </Reveal>
              ))}
            </div>
          )
        ) : (
          <p className="mt-8 text-center text-[#B9C8D5] text-sm" data-testid="catalog-hint">
            Choose a category above to see everything we carry.
          </p>
        )}
      </div>

      {/* Floating "back to categories" pill — appears after scrolling past the tiles */}
      {showBackToTop && (
        <button
          type="button"
          onClick={scrollToCategoryTop}
          className="fixed bottom-24 right-6 md:bottom-28 md:right-8 z-40 flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-[#0A2438]/95 border border-[#FFD166]/45 backdrop-blur-md text-[#F6F1E4] text-xs shadow-[0_10px_30px_rgba(0,0,0,0.55)] hover:border-[#FFD166] transition-all"
          data-testid="back-to-categories"
          aria-label="Back to categories"
        >
          <ArrowUp className="w-3.5 h-3.5 text-[#FFD166]" />
          <span className="hidden sm:inline">Back to categories</span>
          <span className="sm:hidden">Categories</span>
        </button>
      )}

      {/* Product details modal — opens when a compact catalog card is clicked */}
      {detailsProduct && (
        <ProductDetailsModal
          product={detailsProduct}
          range={range}
          scrollTo={detailsScrollTo}
          onClose={() => { setDetailsProduct(null); setDetailsScrollTo(null); }}
          onAdd={addToCart}
          selectedQty={cart[detailsProduct.id] || 0}
          initialOptionIds={cartOptions[detailsProduct.id] || []}
          onToggleCompare={toggleCompare}
          comparing={compareIds.includes(detailsProduct.id)}
        />
      )}

      {/* Stroller comparison — sticky bar + side-by-side modal */}
      {!compareOpen && (
        <CompareBar
          products={compareProducts}
          onRemove={(id) => setCompareIds((cur) => cur.filter((x) => x !== id))}
          onCompare={() => setCompareOpen(true)}
          onClear={() => setCompareIds([])}
        />
      )}
      {compareOpen && compareProducts.length === 2 && (
        <CompareModal
          products={compareProducts}
          onClose={() => setCompareOpen(false)}
        />
      )}
    </section>
  );
};
